import type { QueryClient } from '@tanstack/react-query';
import type { PortfolioClient } from '@personal-portfolio/api-client';
import { portfolioKeys } from './keys.js';

// Warms every portfolio query once at app start.
//
// allSettled, and the returned results are never inspected: the sole caller
// voids this promise. A failed prefetch is not an error — the route's own
// useQuery will run again and surface the failure with its retry/error UI, so
// rejections here are deliberately dropped rather than logged or rethrown.
//
// Each queryFn receives TanStack Query's own AbortSignal (per prefetch, not
// shared) and forwards it to the client, so an unmount cancels the in-flight
// request rather than orphaning it.
export const prefetchPortfolio = (
  queryClient: QueryClient,
  client: PortfolioClient,
): Promise<PromiseSettledResult<void>[]> =>
  Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: portfolioKeys.profile(),
      queryFn: ({ signal }) => client.getProfile({ signal }),
    }),
    queryClient.prefetchQuery({
      queryKey: portfolioKeys.siteContent(),
      queryFn: ({ signal }) => client.getSiteContent({ signal }),
    }),
    queryClient.prefetchQuery({
      queryKey: portfolioKeys.skills(),
      queryFn: ({ signal }) => client.getSkills({ signal }),
    }),
    queryClient.prefetchQuery({
      queryKey: portfolioKeys.education(),
      queryFn: ({ signal }) => client.getEducation({ signal }),
    }),
    queryClient.prefetchQuery({
      queryKey: portfolioKeys.awards(),
      queryFn: ({ signal }) => client.getAwards({ signal }),
    }),
    queryClient.prefetchQuery({
      queryKey: portfolioKeys.certifications(),
      queryFn: ({ signal }) => client.getCertifications({ signal }),
    }),
    queryClient.prefetchQuery({
      queryKey: portfolioKeys.projects(),
      queryFn: ({ signal }) => client.getProjects({ signal }),
    }),
    queryClient.prefetchQuery({
      queryKey: portfolioKeys.experience(),
      queryFn: ({ signal }) => client.getExperience({ signal }),
    }),
  ]);
